import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import axios from "axios";

const paymentModes = [
  { value: "CASH", label: "Cash" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
  { value: "UPI", label: "UPI" },
  { value: "CARD", label: "Credit Card" },
  { value: "CHEQUE", label: "Cheque" },
];

const expenseCategories = [
  { value: "COMMISSION", label: "Commission" },
  { value: "VENDOR_PAYMENT", label: "Vendor Payment" },
  { value: "TAXI_CHARGES", label: "Taxi Charges" },
  { value: "FOOD_EXPENSE", label: "Food Expense" },
  { value: "MAINTENANCE", label: "Maintenance" },
  { value: "SUPPLIES", label: "Supplies" },
  { value: "MISCELLANEOUS", label: "Miscellaneous" },
];

const getCurrency = () => localStorage.getItem("currencycode") || "₹";

const getApiUrl = () =>
  (process.env.REACT_APP_API_URL || "http://localhost:3000/api").replace(/\/+$/, "");

const money = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: n % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

const drawerSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 14,
    borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.12)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(37, 99, 235, 0.12)" : "none",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
    "&:hover": {
      borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.22)",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 12px",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#94a3b8",
    fontWeight: 700,
  }),
  singleValue: (base) => ({
    ...base,
    color: "#0f172a",
    fontWeight: 850,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 2147483647,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 2147483647,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 18px 42px rgba(15, 23, 42, 0.18)",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    fontWeight: state.isSelected ? 900 : 750,
    cursor: "pointer",
  }),
};

const AddEditExpensePopup = ({ onSubmit, expenseData, onCancel }) => {
  const currency = getCurrency();
  const apiUrl = getApiUrl();
  const token = localStorage.getItem("token");
  const isEditMode = Boolean(expenseData);

  const [formValues, setFormValues] = useState({
    amount: "",
    category: null,
    description: "",
    paymentMode: paymentModes[0],
    referenceId: "",
    referenceType: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const title = isEditMode ? "Edit Expense" : "Add Expense";
  const amountPreview = useMemo(() => money(formValues.amount), [formValues.amount]);

  useEffect(() => {
    if (!isEditMode || !expenseData) return;

    setFormValues({
      amount: expenseData.amount?.toString() || "",
      category:
        expenseCategories.find((cat) => cat.value === expenseData.category) ||
        (expenseData.category
          ? { value: expenseData.category, label: String(expenseData.category).replace(/_/g, " ") }
          : null),
      description: expenseData.notes || expenseData.description || "",
      paymentMode:
        paymentModes.find((mode) => mode.value === expenseData.payment_mode) ||
        paymentModes.find((mode) => mode.value === expenseData.paymentMode) ||
        paymentModes[0],
      referenceId: expenseData.reference_id || expenseData.referenceId || "",
      referenceType: expenseData.reference_type || expenseData.referenceType || "",
    });
  }, [expenseData, isEditMode]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && !loading) {
        onCancel?.();
      }
    };

    document.body.classList.add("ns-expense-drawer-open");
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.classList.remove("ns-expense-drawer-open");
      window.removeEventListener("keydown", handleEscape);
    };
  }, [loading, onCancel]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormValues((previous) => ({
      ...previous,
      [name]: value,
    }));

    setErrors((previous) => ({ ...previous, [name]: "" }));
  };

  const handleSelectChange = (name) => (selectedOption) => {
    setFormValues((previous) => ({
      ...previous,
      [name]: selectedOption,
    }));

    setErrors((previous) => ({ ...previous, [name]: "" }));
  };

  const validateForm = () => {
    const validationErrors = {};
    const amount = Number(formValues.amount);

    if (!formValues.amount || Number.isNaN(amount) || amount <= 0) {
      validationErrors.amount = "Enter a valid amount greater than zero.";
    }

    if (!formValues.category) {
      validationErrors.category = "Select an expense category.";
    }

    if (!formValues.paymentMode) {
      validationErrors.paymentMode = "Select a payment mode.";
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const buildPayload = () => {
    const payload = {
      amount: Number(formValues.amount),
      category: formValues.category.value,
      description: formValues.description?.trim() || "",
      paymentMode: formValues.paymentMode.value,
      referenceId: formValues.referenceId?.trim() || "",
      referenceType: formValues.referenceType?.trim() || "",
    };

    if (isEditMode) {
      payload.expenseId = expenseData.transaction_id || expenseData.expenseId || expenseData.id;
    }

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    const endpoint = isEditMode
      ? `${apiUrl}/pos/transactions/edit-expenses`
      : `${apiUrl}/pos/transactions/add-expenses`;

    try {
      setLoading(true);

      const response = await axios.post(endpoint, buildPayload(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success(`Expense ${isEditMode ? "updated" : "added"} successfully.`);
      onSubmit?.(response.data);
    } catch (error) {
      toast.error(
        `Failed to ${isEditMode ? "update" : "add"} expense: ${
          error.response?.data?.message || error.response?.data?.error || "Unknown error"
        }`
      );
      console.error(`Error ${isEditMode ? "updating" : "adding"} expense:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = () => {
    if (!loading) onCancel?.();
  };

  return (
    <div className="ns-expense-drawer-root" role="dialog" aria-modal="true" aria-label={title}>
      <style>{`
        body.ns-expense-drawer-open {
          overflow: hidden !important;
        }

        .ns-expense-drawer-root {
          position: fixed;
          inset: 0;
          z-index: 2147483000;
          display: flex;
          justify-content: flex-end;
          background: rgba(15, 23, 42, 0.08);
          backdrop-filter: blur(1.5px);
          -webkit-backdrop-filter: blur(1.5px);
        }

        .ns-expense-backdrop {
          position: absolute;
          inset: 0;
          cursor: pointer;
          background: transparent;
          border: 0;
          padding: 0;
        }

        .ns-expense-drawer {
          position: relative;
          width: min(520px, 100vw);
          height: 100dvh;
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 32%),
            #f8fafc;
          box-shadow: -18px 0 48px rgba(15, 23, 42, 0.16);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          animation: ns-expense-slide-in 0.18s ease-out;
        }

        @keyframes ns-expense-slide-in {
          from {
            transform: translateX(24px);
            opacity: 0.88;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .ns-expense-header {
          flex: 0 0 auto;
          background: rgba(255, 255, 255, 0.96);
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          padding: 18px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 14px;
        }

        .ns-expense-title-wrap {
          min-width: 0;
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        .ns-expense-icon {
          width: 44px;
          height: 44px;
          border-radius: 16px;
          background: linear-gradient(135deg, #1d4ed8, #2563eb);
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 14px 30px rgba(37, 99, 235, 0.22);
          font-size: 1.25rem;
          font-weight: 950;
          flex: 0 0 auto;
        }

        .ns-expense-eyebrow {
          color: #2563eb;
          font-size: 0.68rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          font-weight: 950;
          margin-bottom: 5px;
        }

        .ns-expense-title {
          color: #001f5b;
          font-size: 1.24rem;
          line-height: 1.15;
          font-weight: 950;
          margin: 0;
        }

        .ns-expense-subtitle {
          color: #64748b;
          font-size: 0.78rem;
          line-height: 1.42;
          font-weight: 760;
          margin: 5px 0 0;
        }

        .ns-expense-close {
          width: 40px;
          height: 40px;
          border-radius: 14px;
          border: 1px solid rgba(220, 38, 38, 0.16);
          background: #fff1f2;
          color: #dc2626;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 1.35rem;
          line-height: 1;
          transition: transform 0.16s ease, background 0.16s ease;
          flex: 0 0 auto;
        }

        .ns-expense-close:hover:not(:disabled) {
          background: #fee2e2;
          transform: translateY(-1px);
        }

        .ns-expense-close:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }

        .ns-expense-summary {
          flex: 0 0 auto;
          margin: 14px 18px 0;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 20px;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
          padding: 14px;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 12px;
          align-items: center;
        }

        .ns-expense-summary span {
          display: block;
          color: #64748b;
          font-size: 0.72rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 3px;
        }

        .ns-expense-summary strong {
          display: block;
          color: #16a34a;
          font-size: 1.35rem;
          line-height: 1.16;
          font-weight: 950;
        }

        .ns-expense-summary-badge {
          border-radius: 999px;
          background: #eff6ff;
          color: #1d4ed8;
          border: 1px solid #bfdbfe;
          padding: 7px 11px;
          font-size: 0.72rem;
          font-weight: 950;
          white-space: nowrap;
        }

        .ns-expense-body {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          padding: 14px 18px 110px;
          scrollbar-width: thin;
          scrollbar-color: #93c5fd #e2e8f0;
        }

        .ns-expense-body::-webkit-scrollbar {
          width: 8px;
        }

        .ns-expense-body::-webkit-scrollbar-track {
          background: #e2e8f0;
          border-radius: 999px;
        }

        .ns-expense-body::-webkit-scrollbar-thumb {
          background: #93c5fd;
          border-radius: 999px;
        }

        .ns-expense-form-grid {
          display: grid;
          gap: 13px;
        }

        .ns-expense-field {
          display: grid;
          gap: 7px;
        }

        .ns-expense-field.two-col {
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .ns-expense-field label,
        .ns-expense-subfield label {
          color: #0f172a;
          font-size: 0.78rem;
          font-weight: 950;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          margin: 0;
        }

        .ns-expense-required {
          color: #dc2626;
          font-weight: 950;
        }

        .ns-expense-input,
        .ns-expense-textarea {
          width: 100%;
          border-radius: 14px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          background: #ffffff;
          color: #0f172a;
          font-size: 0.88rem;
          font-weight: 760;
          outline: none;
          transition: border-color 0.16s ease, box-shadow 0.16s ease;
        }

        .ns-expense-input {
          min-height: 44px;
          padding: 0 13px;
        }

        .ns-expense-textarea {
          min-height: 108px;
          padding: 12px 13px;
          resize: vertical;
        }

        .ns-expense-input:focus,
        .ns-expense-textarea:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }

        .ns-expense-error {
          color: #dc2626;
          font-size: 0.72rem;
          font-weight: 820;
          line-height: 1.35;
        }

        .ns-expense-hint {
          color: #94a3b8;
          font-size: 0.72rem;
          line-height: 1.4;
          font-weight: 700;
        }

        .ns-expense-footer {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.96);
          border-top: 1px solid rgba(15, 23, 42, 0.08);
          box-shadow: 0 -14px 34px rgba(15, 23, 42, 0.08);
          padding: 13px 18px;
          display: grid;
          grid-template-columns: 1fr auto auto;
          gap: 10px;
          align-items: center;
        }

        .ns-expense-footer-total span {
          display: block;
          color: #64748b;
          font-size: 0.7rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .ns-expense-footer-total strong {
          display: block;
          color: #16a34a;
          font-size: 1.05rem;
          line-height: 1.16;
          font-weight: 950;
        }

        .ns-expense-btn {
          min-height: 42px;
          border-radius: 14px;
          padding: 0 16px;
          border: 1px solid rgba(15, 23, 42, 0.10);
          font-size: 0.82rem;
          font-weight: 950;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          transition: transform 0.16s ease, box-shadow 0.16s ease, opacity 0.16s ease;
          white-space: nowrap;
        }

        .ns-expense-btn:hover:not(:disabled) {
          transform: translateY(-1px);
        }

        .ns-expense-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .ns-expense-btn.secondary {
          background: #ffffff;
          color: #334155;
        }

        .ns-expense-btn.primary {
          background: linear-gradient(135deg, #16a34a, #22c55e);
          border-color: #16a34a;
          color: #ffffff;
          box-shadow: 0 14px 30px rgba(22, 163, 74, 0.20);
        }

        @media (max-width: 760px) {
          .ns-expense-drawer {
            width: 100vw;
          }

          .ns-expense-field.two-col {
            grid-template-columns: 1fr;
          }

          .ns-expense-footer {
            grid-template-columns: 1fr;
          }

          .ns-expense-btn {
            width: 100%;
          }
        }
      `}</style>

      <button
        type="button"
        className="ns-expense-backdrop"
        aria-label="Close expense drawer"
        onClick={handleBackdropClick}
        disabled={loading}
      />

      <form className="ns-expense-drawer" onSubmit={handleSubmit}>
        <header className="ns-expense-header">
          <div className="ns-expense-title-wrap">
            <span className="ns-expense-icon">{isEditMode ? "↻" : "+"}</span>
            <div>
              <div className="ns-expense-eyebrow">POS Expense Control</div>
              <h5 className="ns-expense-title">{title}</h5>
              <p className="ns-expense-subtitle">
                Record operational expenses with category, payment mode, reference, and audit-ready notes.
              </p>
            </div>
          </div>

          <button
            type="button"
            className="ns-expense-close"
            onClick={onCancel}
            disabled={loading}
            aria-label="Close"
            title="Close"
          >
            ×
          </button>
        </header>

        <div className="ns-expense-summary">
          <div>
            <span>Expense Amount</span>
            <strong>
              {currency}
              {amountPreview}
            </strong>
          </div>
          <div className="ns-expense-summary-badge">
            {formValues.paymentMode?.label || "Payment Mode"}
          </div>
        </div>

        <main className="ns-expense-body">
          <div className="ns-expense-form-grid">
            <div className="ns-expense-field">
              <label htmlFor="expense-amount">
                Amount <span className="ns-expense-required">Required</span>
              </label>
              <input
                id="expense-amount"
                type="number"
                name="amount"
                value={formValues.amount}
                onChange={handleInputChange}
                className="ns-expense-input"
                placeholder="Enter amount"
                min="0"
                step="0.01"
                inputMode="decimal"
              />
              {errors.amount ? <small className="ns-expense-error">{errors.amount}</small> : null}
            </div>

            <div className="ns-expense-field">
              <label>
                Category <span className="ns-expense-required">Required</span>
              </label>
              <Select
                options={expenseCategories}
                onChange={handleSelectChange("category")}
                value={formValues.category}
                isSearchable
                placeholder="Select expense category"
                styles={drawerSelectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                menuPlacement="auto"
                menuShouldScrollIntoView={false}
              />
              {errors.category ? <small className="ns-expense-error">{errors.category}</small> : null}
            </div>

            <div className="ns-expense-field">
              <label>
                Payment Mode <span className="ns-expense-required">Required</span>
              </label>
              <Select
                options={paymentModes}
                onChange={handleSelectChange("paymentMode")}
                value={formValues.paymentMode}
                isSearchable={false}
                placeholder="Select payment mode"
                styles={drawerSelectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                menuPlacement="auto"
                menuShouldScrollIntoView={false}
              />
              {errors.paymentMode ? <small className="ns-expense-error">{errors.paymentMode}</small> : null}
            </div>

            <div className="ns-expense-field">
              <label htmlFor="expense-description">Description / Notes</label>
              <textarea
                id="expense-description"
                name="description"
                value={formValues.description}
                onChange={handleInputChange}
                className="ns-expense-textarea"
                placeholder="Example: vendor payment, repair work, taxi charges, or supplies purchase"
                rows={4}
              />
              <span className="ns-expense-hint">
                Keep this clear. It helps day-close review and expense audit later.
              </span>
            </div>

            <div className="ns-expense-field two-col">
              <div className="ns-expense-subfield">
                <label htmlFor="expense-reference-id">Reference ID</label>
                <input
                  id="expense-reference-id"
                  type="text"
                  name="referenceId"
                  value={formValues.referenceId}
                  onChange={handleInputChange}
                  className="ns-expense-input"
                  placeholder="Optional"
                />
              </div>

              <div className="ns-expense-subfield">
                <label htmlFor="expense-reference-type">Reference Type</label>
                <input
                  id="expense-reference-type"
                  type="text"
                  name="referenceType"
                  value={formValues.referenceType}
                  onChange={handleInputChange}
                  className="ns-expense-input"
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>
        </main>

        <footer className="ns-expense-footer">
          <div className="ns-expense-footer-total">
            <span>Total Expense</span>
            <strong>
              {currency}
              {amountPreview}
            </strong>
          </div>

          <button
            type="button"
            className="ns-expense-btn secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>

          <button type="submit" className="ns-expense-btn primary" disabled={loading}>
            {loading ? "Saving..." : isEditMode ? "Update Expense" : "Save Expense"}
          </button>
        </footer>
      </form>
    </div>
  );
};

export default AddEditExpensePopup;
